"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { createContext, ReactNode, useContext, useMemo } from "react";
import type { ChainInfo } from "@/app/api/chains/shared";
interface RegistryContextValue {
  getChain: (chainId: number) => ChainInfo | null;
  isLoading: boolean;
  error: Error | null;
}
const ChainRegistryContext = createContext<RegistryContextValue>({
  getChain: () => null,
  isLoading: false,
  error: null
});

/**
 * Namespaced under `chainInfo` so a blanket `invalidate(["chainInfo"])`
 * (the value of `chainInfoKeys.all`) also flushes the registry.
 */
export const chainRegistryQueryKey = ["chainInfo", "registry"] as const;
async function fetchChainRegistry(): Promise<Record<number, ChainInfo>> {
  const res = await fetch("/api/chains/registry");
  if (!res.ok) throw new Error(`Chain registry failed: ${res.status}`);
  const body = (await res.json()) as {
    chains: Record<string, ChainInfo>;
  };
  const out: Record<number, ChainInfo> = {};
  for (const [idStr, info] of Object.entries(body.chains)) {
    const id = Number(idStr);
    if (!Number.isFinite(id)) continue;
    out[id] = info;
  }
  return out;
}

/**
 * Loads the chain metadata registry once per session and exposes O(1)
 * lookups via context. Components use `useChainInfo(id)` to read from
 * this registry — no per-chain network calls.
 *
 * Must be mounted inside QueryClientProvider and above routes that need
 * chain metadata (which is everywhere).
 */
export function ChainRegistryProvider(t0) {
  const $ = _c(12);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: chainRegistryQueryKey,
      queryFn: fetchChainRegistry,
      staleTime: 3600000,
      gcTime: 86400000,
      retry: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data,
    isLoading,
    error
  } = useQuery(t1);
  let t2;
  let t3;
  if ($[1] !== data) {
    t3 = data ?? {};
    $[1] = data;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const chains = t3;
  let t4;
  if ($[3] !== chains) {
    t4 = chainId => chains[chainId] ?? null;
    $[3] = chains;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = error as Error | null;
  let t6;
  if ($[5] !== isLoading || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      getChain: t4,
      isLoading,
      error: t5
    };
    $[5] = isLoading;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  t2 = t6;
  const value = t2;
  let t7;
  if ($[9] !== children || $[10] !== value) {
    t7 = <ChainRegistryContext.Provider value={value}>{children}</ChainRegistryContext.Provider>;
    $[9] = children;
    $[10] = value;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
export function useChainRegistry() {
  return useContext(ChainRegistryContext);
}